import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Store, Star, MapPin, Package, MessageCircle, Trophy, BadgeCheck, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../components/layout/Layout';
import ProductCard from '../components/product/ProductCard';
import SEO from '../components/common/SEO';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StorePage = () => {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStore = async () => {
      try {
        const response = await fetch(`${API_URL}/api/stores/${storeId}`);
        if (response.ok) {
          const data = await response.json();
          setStore(data.store);
          setProducts(data.products);
        }
      } catch (error) {
        console.error('Error fetching store:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStore();
  }, [storeId]);

  if (loading) {
    return (
      <Layout showBack title="Store">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-minted-600" />
        </div>
      </Layout>
    );
  }

  if (!store) {
    return (
      <Layout showBack title="Store">
        <div className="flex flex-col items-center justify-center h-64 text-gray-500">
          <Store className="w-12 h-12 mb-2" />
          <p>Store not found</p>
        </div>
      </Layout>
    );
  }

  const handleShare = async () => {
    // OG preview endpoint gives socials the store name, description & image, then redirects
    const shareUrl = `${window.location.origin}/api/share/store/${storeId}`;
    const shareData = {
      title: `${store.name} on Minted`,
      text: `Shop ${store.name} on Minted — ${products.length} pieces available`,
      url: shareUrl
    };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch {}
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Boutique link copied — paste it anywhere!');
      } catch {
        toast.error('Could not copy link');
      }
    }
  };

  return (
    <Layout showBack>
      <SEO
        title={store.name}
        description={store.description || `Shop ${store.name} on Minted - ${products.length} items available`}
        url={`/store/${storeId}`}
      />

      {/* Boutique hero */}
      <div className="relative bg-[#1a3a2a] text-white px-5 pt-7 pb-8 overflow-hidden" data-testid="store-hero">
        <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#d4af37]/70 to-transparent" />

        <div className="relative flex flex-col items-center text-center">
          {/* Gold monogram-ring avatar */}
          <div className="relative mb-3">
            <div className="w-24 h-24 rounded-full p-[3px] bg-gradient-to-br from-[#d4af37] via-[#f3e5b0] to-[#b8912a]">
              {store.avatar ? (
                <img src={store.avatar} alt={store.name} className="w-full h-full rounded-full object-cover border-[3px] border-[#1a3a2a]" />
              ) : (
                <div className="w-full h-full rounded-full bg-[#2c5a45] border-[3px] border-[#1a3a2a] flex items-center justify-center">
                  <span className="font-display text-2xl text-[#f3e5b0]">
                    {(store.name || 'M').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                  </span>
                </div>
              )}
            </div>
            {store.is_founding_seller && (
              <div className="absolute -bottom-1 -right-1 bg-[#d4af37] rounded-full p-1.5 shadow-lg" title="Founding Seller">
                <Trophy className="w-4 h-4 text-[#1a3a2a]" />
              </div>
            )}
          </div>

          {store.joined_date && (
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#d4af37] mb-1">
              Est. {new Date(store.joined_date).getFullYear()}
            </p>
          )}
          <h1 className="font-display text-2xl leading-tight flex items-center gap-2">
            {store.name}
            <BadgeCheck className="w-5 h-5 text-emerald-300" />
          </h1>
          {store.location && (
            <div className="flex items-center gap-1 text-emerald-200/80 text-xs mt-1">
              <MapPin className="w-3 h-3" />
              {store.location}
            </div>
          )}

          {store.description && (
            <p className="mt-3 text-emerald-100/90 text-sm max-w-sm leading-relaxed">{store.description}</p>
          )}

          {/* Stats plaque */}
          <div className="mt-5 w-full max-w-sm grid grid-cols-3 divide-x divide-white/10 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm py-3">
            <div>
              <p className="font-display text-xl text-[#f3e5b0] leading-none">{products.length}</p>
              <p className="text-[9px] uppercase tracking-[0.2em] text-emerald-200/70 mt-1">Pieces</p>
            </div>
            <div>
              <p className="font-display text-xl text-[#f3e5b0] leading-none flex items-center justify-center gap-1">
                <Star className="w-3.5 h-3.5 text-[#d4af37] fill-[#d4af37]" />
                {store.rating > 0 ? store.rating.toFixed(1) : 'New'}
              </p>
              <p className="text-[9px] uppercase tracking-[0.2em] text-emerald-200/70 mt-1">{store.review_count || 0} Reviews</p>
            </div>
            <div>
              <p className="font-display text-xl text-[#f3e5b0] leading-none">{store.sales_count || 0}</p>
              <p className="text-[9px] uppercase tracking-[0.2em] text-emerald-200/70 mt-1">Sold</p>
            </div>
          </div>

          <div className="mt-5 w-full max-w-sm flex gap-2">
            <Button
              className="flex-1 bg-transparent hover:bg-[#d4af37]/10 text-[#f3e5b0] border border-[#d4af37]/60 rounded-xl h-11 transition-colors"
              onClick={() => navigate(`/messages?seller=${storeId}`)}
              data-testid="contact-store-btn"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Contact Boutique
            </Button>
            <Button
              className="w-11 h-11 p-0 bg-[#d4af37] hover:bg-[#c9a532] text-[#1a3a2a] rounded-xl transition-colors flex-shrink-0"
              onClick={handleShare}
              aria-label="Share this boutique"
              data-testid="share-store-btn"
            >
              <Share2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="p-4 bg-[#f7f5f2] min-h-[40vh]">
        <div className="flex items-baseline gap-2 mb-4">
          <h2 className="font-display text-lg text-[#1a3a2a]">The Collection</h2>
          <span className="text-xs text-[#a8a29e] uppercase tracking-[0.2em]">{products.length} pieces</span>
        </div>
        
        {products.length > 0 ? (
          <div className="grid grid-cols-2 gap-3">
            {products.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <Package className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>No pieces available right now</p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default StorePage;
